import { IBrowser, ICPU, IDevice, IEngine, IOS, UAParser } from 'ua-parser-js';

let navigator: any;

try {
  if (typeof window !== 'undefined') navigator = window.navigator;
} catch (error) {
  console.error(error);
}

function isBrowserStandaloneMode() {
  return navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
}

async function getBatteryInfo() {
  const deviceBattery: any = {};

  const validateBattery = (batteryMetaData: any) => batteryMetaData ?? null;
  if ('getBattery' in navigator) {
    const batteryStatus = await navigator.getBattery();
    deviceBattery.charging = validateBattery(batteryStatus.charging);
    deviceBattery.chargingTime = validateBattery(batteryStatus.chargingTime);
    deviceBattery.dischargingTime = validateBattery(batteryStatus.dischargingTime);
    deviceBattery.level = validateBattery(batteryStatus.level);
  }
  return deviceBattery;
}

function isIOSSafari() {
  const ua = navigator.userAgent;
  return (!!ua.match(/iPad/i) || !!ua.match(/iPhone/i)) && !!ua.match(/WebKit/i) && !ua.match(/CriOS/i);
}

// This one is based on https://bugs.webkit.org/show_bug.cgi?id=167341
// could probably be useful if somehow the same bug reappears especially in ios
function downloadBlob(blob: Blob, filename: string): Promise<boolean> {
  const createButtonAndClick = (url: string) => {
    const aLink = document.createElement('a');
    aLink.setAttribute('download', filename);
    aLink.setAttribute('href', url);

    document.body.appendChild(aLink);
    aLink.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    aLink.remove();
  };

  return new Promise((resolve, reject) => {
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, filename);
    } else {
      if (window.FileReader && !navigator.userAgent.match('FxiOS')) {
        const reader = new FileReader();
        reader.onload = function () {
          let url = reader.result as string;
          if (isIOSSafari()) url = url.replace(/^data:[^;]*;/, 'data:attachment/file;');
          createButtonAndClick(url);
        };

        reader.onerror = function (event) {
          reject(`Failed downloading the pdf file ${event.type}`);
        };

        reader.readAsDataURL(blob);
      } else {
        // Can only open the file in new tab for firefox ios
        const windowUrl = window.URL || window.webkitURL || null;
        if (windowUrl) {
          const url = windowUrl.createObjectURL(blob);
          if (url) {
            createButtonAndClick(url);

            setTimeout(function () {
              windowUrl.revokeObjectURL(url);
            }, 250);

            resolve(true);
          }
        }

        resolve(false);
      }
    }

    resolve(true);
  });
}

async function hasBluetooth() {
  let hasBluetooth = null;
  if ('bluetooth' in navigator && 'getAvailability' in navigator.bluetooth) {
    hasBluetooth = navigator.bluetooth ? await navigator.bluetooth.getAvailability() : false;
  }
  return hasBluetooth;
}

function getNetworkConnectionType() {
  const validateNetworkConnection = (connectionProperty: string, navigatorProperty = 'connection') =>
    navigatorProperty in navigator && connectionProperty in navigator.connection ? navigator.connection[connectionProperty] : null;

  return {
    type: validateNetworkConnection('type'),
    downlink: validateNetworkConnection('downlink'),
    rtt: validateNetworkConnection('rtt'),
    downlinkMax: validateNetworkConnection('downlinkMax'),
    effectiveType: validateNetworkConnection('effectiveType'),
    saveData: validateNetworkConnection('saveData'),
  };
}

async function getNetworkPermissions() {
  const permissions: any = {};

  try {
    if ('permissions' in navigator && 'query' in navigator.permissions) {
      const geolocationStatus = await navigator.permissions.query({
        name: 'geolocation',
      });
      permissions.geolocation = geolocationStatus.state;
      const notificationsStatus = await navigator.permissions.query({
        name: 'notifications',
      });
      permissions.notifications = notificationsStatus.state;
      const pushStatus = await navigator.permissions.query({
        name: 'push',
        userVisibleOnly: true,
      });
      permissions.push = pushStatus.state;
      const persistentStorageStatus = await navigator.permissions.query({
        name: 'persistent-storage',
        userVisibleOnly: true,
      });
      permissions['persistent-storage'] = persistentStorageStatus.state;
    }
  } catch (error) {
    console.error(`Error getting network permissions: ${error.message}`);
  }
  return permissions;
}

function getClientUUID() {
  let uid = '';
  try {
    const navigator_info = window.navigator;
    const screen_info = window.screen;
    uid = navigator_info?.mimeTypes?.length?.toString() ?? '';
    uid += navigator_info.userAgent.replace(/\D+/g, '');
    uid += navigator_info.plugins.length;
    uid += screen_info.height || '';
    uid += screen_info.width || '';
    uid += screen_info.pixelDepth || '';
  } catch (e) {
    console.error(e);
  }
  return uid;
}

async function getBrowserData() {
  const browserData = new UAParser();
  const data = browserData.getResult();
  const browserDataObj: {
    ua?: string;
    language?: string;
    device?: IDevice & { bluetooth?: any; memory?: any; platform?: any; battery?: any; connection?: any; nfc?: any };
    browser?: IBrowser & { uuid?: string; permissions?: any };
    cpu?: ICPU;
    os?: IOS;
    engine?: IEngine;
  } = {};

  try {
    browserDataObj.ua = 'userAgent' in navigator ? navigator.userAgent : null;

    browserDataObj.language = 'language' in navigator ? navigator.language : null;

    browserDataObj.device = data.device;
    browserDataObj.browser = data.browser;
    browserDataObj.cpu = data.cpu;
    browserDataObj.os = data.os;
    browserDataObj.engine = data.engine;

    browserDataObj.browser.uuid = getClientUUID();
    browserDataObj.browser.permissions = await getNetworkPermissions();

    browserDataObj.device.bluetooth = await hasBluetooth();
    browserDataObj.device.memory = 'deviceMemory' in navigator ? navigator.deviceMemory : null;
    browserDataObj.device.platform = 'platform' in navigator ? navigator.platform : null;
    browserDataObj.device.battery = await getBatteryInfo();

    browserDataObj.device.connection = getNetworkConnectionType();
    browserDataObj.device.nfc = 'NDEFReader' in window ? true : false;
  } catch (error) {
    console.error(`Error getting browser data: ${error.message}`);
  }
  return browserDataObj;
}

function checkIsNarrowSymbolSupported() {
  let ua: string;

  if (typeof navigator !== 'undefined') ua = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);

  if (isSafari) {
    const safariVersion = ua.substring(ua.indexOf('Version/') + 8).split(' ')[0];
    const safariMajor = safariVersion.split('.')[0];
    const safariMinor = safariVersion.split('.')[1];

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#browser_compatibility
    const supportedMajor = 14;
    const supportedMinor = isIOSSafari() ? 5 : 1;

    if (parseInt(safariMajor) < supportedMajor || (parseInt(safariMajor) === supportedMajor && parseInt(safariMinor) < supportedMinor)) return false;
  }

  return true;
}

export {
  getBrowserData,
  getClientUUID,
  getNetworkPermissions,
  isIOSSafari,
  getNetworkConnectionType,
  hasBluetooth,
  getBatteryInfo,
  isBrowserStandaloneMode,
  downloadBlob,
  checkIsNarrowSymbolSupported,
};
